import { z } from 'zod';
import { ApiError, type ApiOpts } from '@/lib/api';
/** Component-scoped, memory-only reads. Never changes authentication or the global API adapter. */
export function createLmsDemoApi(
  kind: 'school' | 'tutor',
  state = 'reviewed',
  practice = false,
  coco = false,
) {
  const now = Date.now(),
    at = (days: number) => new Date(now + days * 86400000).toISOString();
  const today = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Australia/Brisbane',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(now));
  const monday = new Date(`${today}T00:00:00Z`);
  monday.setUTCDate(monday.getUTCDate() - ((monday.getUTCDay() + 6) % 7));
  const slots: number[] = [];
  for (let week = -2; week <= 2; week++)
    for (const day of [0, 3]) {
      const time = new Date(monday);
      time.setUTCDate(time.getUTCDate() + week * 7 + day);
      time.setUTCHours(kind === 'school' ? 5 : 6, kind === 'school' ? 30 : 0, 0, 0);
      slots.push(time.getTime());
    }
  const next = slots.find((time) => time + 3600000 > now)!;
  const previous = slots.filter((time) => time + 3600000 <= now).at(-1)!;
  const scheme = {
    kind: coco ? 'feedback' : 'points',
    maximum: coco ? undefined : 10,
    criteria: ['Explain the method', 'Check the result'],
  };
  const assignment = {
    id: 'demo-lms-assignment-1',
    title: coco ? 'Solve a linear equation' : 'Explain a fraction comparison',
    instructions: coco
      ? 'Solve 3x + 5 = 20. Show an inverse operation at each step, then check your result.'
      : 'Compare 3/4 and 2/3. Explain your method and check it with a second representation.',
    status: 'published',
    due_at: coco ? '2026-10-22T07:00:00Z' : at(2),
    config: {
      scheme,
      response_types: ['text', 'image', 'project'],
      allow_resubmit: true,
      material_snapshot: {
        lesson: { title: coco ? 'Linear equations' : 'Comparing fractions' },
        materials: [],
      },
    },
  };
  const body = {
    text: coco
      ? '3x + 5 = 20\nSubtract 5 from both sides: 3x = 15.\nDivide both sides by 3: x = 5.\nCheck: 3 × 5 + 5 = 15 + 5 = 20.\nThe left side equals the right side, so x = 5 is a solution.'
      : '3/4 is 9/12 and 2/3 is 8/12, so 3/4 is larger.',
    attachment_ids: [] as string[],
    project_ids: [] as string[],
    project_versions: [] as Array<{
      id: string;
      project_id: string;
      title: string;
      version: number;
    }>,
  };
  const review = {
    id: 'demo-review-1',
    version: 1,
    action: state === 'returned' ? 'returned' : 'published',
    result: state === 'returned' || coco ? {} : { points: 8 },
    scheme,
    feedback:
      state === 'returned'
        ? coco
          ? 'Show each inverse operation before checking your answer.'
          : 'Show a second representation and resubmit.'
        : coco
          ? 'You applied subtraction and division to both sides, then checked x = 5 in the original equation. Next, explain that these operations keep the two sides equal.'
          : 'Clear common-denominator method. Add a visual check.',
    created_at: coco ? '2026-10-23T03:00:00Z' : at(-1),
  };
  const access = {
    id: 'demo-school-access',
    kid: { id: 'demo-student-1', nickname: 'Alex Chen' },
    institution: {
      id: `demo-${kind}`,
      name: coco
        ? 'Coco · Demo'
        : kind === 'school'
          ? 'Brightwood Learning · Demo'
          : 'Willow Tutoring · Demo',
    },
  };
  const revision = {
    id: 'demo-revision-1',
    number: 1,
    body,
    late: false,
    submitted_at: coco ? '2026-10-21T07:00:00Z' : at(-1),
    reviews: state === 'submitted' || state === 'draft' ? [] : [review],
  };
  const replays = new Map<string, { hash: string; version: number }>();
  const writeSchema = z
    .object({
      expected_version: z.number().int().nonnegative(),
      request_key: z.string().uuid(),
      acknowledge_visibility: z.literal(true),
      body: z
        .object({
          text: z.string().max(12000),
          attachment_ids: z.array(z.string()).max(8),
          project_ids: z.array(z.string()).max(3),
        })
        .strict(),
    })
    .strict();
  const reads: Record<string, unknown> = {
    '/study/learning-plans': {
      items: [
        {
          id: 'demo-plan-1',
          title: coco
            ? 'Coco: solve and check linear equations'
            : 'Compare and explain equivalent fractions',
          subject_key: 'mathematics',
          version: 2,
          weeks: 4,
          starts_on: today,
          objectives: [
            coco
              ? 'Solve 3x + 5 = 20 with inverse operations'
              : 'Compare 3/4 and 2/3 using equivalent fractions',
            coco
              ? 'Explain each inverse operation and verify by substitution'
              : 'Explain and check a comparison with equal-length fraction strips',
          ],
          tasks: [
            {
              key: 'demo-task-1',
              week: 1,
              title: assignment.title,
              instructions: assignment.instructions,
              due_at: assignment.due_at,
              assignment_id: assignment.id,
            },
          ],
        },
      ],
    },
    '/study/lms/assignments/demo-lms-assignment-1': {
      assignment,
      submission: { state, version: 2, draft: body },
      revisions: state === 'draft' ? [] : [revision],
      files: [],
      visibility_notice:
        'Only the work you submit for this assignment is shared with its teacher. Private Study stays private.',
    },
    '/kids/demo-student-1/projects': [
      { id: 'demo-project-1', title: 'Fraction strips', vfs_version: 2 },
    ],
    '/families/me/school-learning': [access],
    '/families/me/school-learning/demo-school-access': {
      access,
      lessons: [
        {
          id: 'demo-session',
          scheduled_starts_at: new Date(next).toISOString(),
          scheduled_ends_at: new Date(next + 3600000).toISOString(),
          class: { name: kind === 'school' ? 'Maths Explorers' : 'Year 7 Maths' },
        },
      ],
      attendance: [
        { status: 'present', register: { class_day: { date: new Date(previous).toISOString() } } },
      ],
      assignments: [
        {
          assignment,
          state: 'reviewed',
          review: { ...review, action: 'published', result: { points: 8 } },
          revision_id: revision.id,
          body,
        },
        {
          assignment: {
            ...assignment,
            id: 'demo-lms-assignment-overdue',
            title: 'Review equivalent fractions',
            due_at: at(-2),
          },
          state: 'reviewed',
          review: {
            id: 'demo-overdue-review-0',
            action: 'published',
            result: { points: 0 },
            scheme,
            feedback:
              'Comparing denominators alone is not a valid method. Convert both fractions to twelfths.',
            created_at: at(-1),
          },
          revision_id: 'demo-overdue-revision',
          body: {
            text: 'I compared the denominators without converting the fractions.',
            attachment_ids: [],
            project_ids: [],
            project_versions: [],
          },
        },
      ],
      reports: [
        {
          id: 'demo-report',
          starts_on: at(-14),
          ends_on: at(0),
          published_at: at(0),
          comment:
            'Alex explains equivalent fractions clearly. Practise checking with a visual model.',
          snapshot: {
            evidence_coverage: '2 reviewed assignments in this example. Activity is not mastery.',
            results: [
              {
                assignment_title: assignment.title,
                result: { points: 8 },
                scheme,
                feedback: review.feedback,
              },
              {
                assignment_title: 'Review equivalent fractions',
                result: { points: 0 },
                scheme,
                feedback:
                  'Comparing denominators alone is not a valid method. Convert both fractions to twelfths.',
              },
            ],
            attendance: [{ status: 'present' }],
          },
        },
      ],
      notices: [
        {
          id: 'demo-receipt',
          status: 'available',
          acknowledged_at: null,
          notice: {
            title: 'Next lesson: fraction models',
            body: 'Bring your fraction strips to the next lesson.',
            created_at: at(-1),
          },
        },
      ],
    },
  };
  if (coco) {
    const first = {
      ...assignment,
      id: 'alex-task-a',
      title: 'Task A · Solve and check',
      due_at: '2026-10-22T07:00:00Z',
    };
    const second = {
      ...assignment,
      id: 'alex-task-b',
      title: 'Task B · Revise the equation setup',
      instructions:
        'A rectangle has perimeter 30 cm. Its length is x + 3 cm and width is x cm. Write and solve an equation; label your units.',
      due_at: '2026-10-24T07:00:00Z',
    };
    const returnedBody = {
      ...body,
      text: 'I wrote x + 3 + x = 30, so 2x + 3 = 30. I need to check whether I included every side.',
    };
    reads['/study/lms/assignments/alex-task-a'] = {
      assignment: first,
      submission: { state, version: 2, draft: body },
      revisions: state === 'draft' ? [] : [revision],
      files: [],
      visibility_notice: 'Only submitted assignment work is shared with your teacher.',
    };
    reads['/study/lms/assignments/alex-task-b'] = {
      assignment: second,
      submission: { state: 'returned', version: 2, draft: returnedBody },
      revisions: [
        {
          ...revision,
          id: 'rectangle-revision',
          body: returnedBody,
          reviews: [
            {
              ...review,
              id: 'rectangle-review',
              action: 'returned',
              result: {},
              feedback:
                'The perimeter includes two lengths and two widths. Start with 2(x + 3) + 2x = 30. Explain why x + 3 + x = 30 does not represent the full perimeter. Submit your revised working.',
            },
          ],
        },
      ],
      files: [],
      visibility_notice: 'Only submitted assignment work is shared with your teacher.',
    };
    reads['/study/learning-plans'] = {
      items: [
        {
          id: 'algebra-plan',
          title: 'Linear equations: explain every step',
          subject_key: 'mathematics',
          version: 2,
          weeks: 4,
          starts_on: '2026-10-19',
          objectives: [
            'Solve one- and two-step equations using balanced operations.',
            'Check a solution by substitution and explain the reasoning.',
          ],
          tasks: [
            {
              key: 'a',
              week: 1,
              title: first.title,
              instructions: first.instructions,
              due_at: first.due_at,
              assignment_id: first.id,
              available: true,
            },
            {
              key: 'b',
              week: 1,
              title: second.title,
              instructions: second.instructions,
              due_at: second.due_at,
              assignment_id: second.id,
              available: true,
            },
            {
              key: 'c',
              week: 2,
              title: 'Explain a new example',
              instructions:
                'Discuss your method with your teacher and write an explanation in your own words.',
              due_at: '2026-10-29T07:00:00Z',
              assignment_id: null,
              available: false,
              opens_at: '2026-10-25T14:00:00Z',
            },
            {
              key: 'd',
              week: 3,
              title: 'Apply the method independently',
              instructions:
                'Your teacher will publish the exact practice after reviewing this week’s work.',
              due_at: '2026-11-05T07:00:00Z',
              assignment_id: null,
              available: false,
              opens_at: '2026-11-01T14:00:00Z',
            },
            {
              key: 'e',
              week: 4,
              title: 'Review the goal with evidence',
              instructions:
                'Choose two pieces of work and explain what changed. Your teacher will review the goal separately from task submission.',
              due_at: '2026-11-12T07:00:00Z',
              assignment_id: null,
              available: false,
              opens_at: '2026-11-08T14:00:00Z',
            },
          ],
        },
      ],
    };
  }
  return async function request<T>(path: string, opts: ApiOpts = {}): Promise<T> {
    if (practice && opts.method && opts.method !== 'GET') {
      if (
        path === '/study/lms/assignments/demo-lms-assignment-1/draft' ||
        path === '/study/lms/assignments/demo-lms-assignment-1/submit'
      ) {
        const input = writeSchema.parse(opts.body),
          entry = reads['/study/lms/assignments/demo-lms-assignment-1'] as {
            submission: { state: string; version: number; draft: typeof body };
            revisions: Array<typeof revision>;
          };
        const hash = JSON.stringify({ path, input }),
          prior = replays.get(input.request_key);
        if (prior) {
          if (prior.hash !== hash)
            throw new ApiError(
              409,
              'LMS_REQUEST_KEY_REUSED',
              'The demo request key was already used.',
            );
          return { version: prior.version, demo: true } as T;
        }
        if (entry.submission.version !== input.expected_version)
          throw new ApiError(
            409,
            'LMS_RECORD_CHANGED',
            'The demo record changed. Your input is retained.',
          );
        if (!['draft', 'returned'].includes(entry.submission.state))
          throw new ApiError(
            409,
            'LMS_WORK_ALREADY_SUBMITTED',
            'Choose the returned-work example before revising.',
          );
        if (
          input.body.attachment_ids.length ||
          input.body.project_ids.some((id) => id !== 'demo-project-1')
        )
          throw new ApiError(
            400,
            'DEMO_UNAVAILABLE',
            'Only the bundled project can be selected in local practice; private image uploads require your real account.',
          );
        const next = {
          ...input.body,
          project_versions: input.body.project_ids.map((id) => ({
            id: 'demo-project-version-2',
            project_id: id,
            title: 'Fraction strips',
            version: 2,
          })),
        };
        const final = path.endsWith('/submit');
        if (final && !next.text.trim() && !next.project_ids.length)
          throw new ApiError(400, 'LMS_EMPTY_SUBMISSION', 'Add a response before submitting.');
        entry.submission.draft = structuredClone(next);
        entry.submission.version++;
        if (final) {
          entry.submission.state = entry.revisions.length ? 'resubmitted' : 'submitted';
          entry.revisions.unshift({
            id: `demo-local-revision-${entry.revisions.length + 1}`,
            number: entry.revisions.length + 1,
            body: structuredClone(next),
            late: false,
            submitted_at: new Date(now).toISOString(),
            reviews: [],
          });
        }
        replays.set(input.request_key, { hash, version: entry.submission.version });
        return { version: entry.submission.version, demo: true } as T;
      }
      if (path === '/families/me/school-learning/notices/demo-receipt/acknowledge') {
        const view = reads['/families/me/school-learning/demo-school-access'] as {
          notices: Array<{ id: string; status: string; acknowledged_at: string | null }>;
        };
        const notice = view.notices[0];
        notice.status = 'acknowledged';
        notice.acknowledged_at ??= new Date(now).toISOString();
        return structuredClone(notice) as T;
      }
    }
    if (opts.method && opts.method !== 'GET')
      throw new ApiError(
        403,
        'DEMO_READ_ONLY',
        'This demo is read-only. No work, invitation or acknowledgement was saved.',
      );
    if (!Object.hasOwn(reads, path))
      throw new ApiError(
        404,
        'DEMO_UNAVAILABLE',
        'This record is not included in the fictional demo.',
      );
    return structuredClone(reads[path]) as T;
  };
}
